import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useRegistration } from '../../contexts/RegistrationContext';
import StepIndicator from './StepIndicator';
import BusinessActivity from './Steps/BusinessActivity';
import CompanyName from './Steps/CompanyName';
import OwnersInfo from './Steps/OwnersInfo';
import Charter from './Steps/Charter';
import AdditionalServices from './Steps/AdditionalServices';
import Summary from './Steps/Summary';

const WizardContainer = styled.div`
  background: white;
  border-radius: 20px;
  border: 2px solid #000;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  overflow: hidden;
  max-width: 800px;
  margin: 2rem auto;
  width: 100%;
  box-sizing: border-box;
`;

const WizardContent = styled.div`
  padding: 2.5rem;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 1.1rem;
  color: #666;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  text-align: center;
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 1.1rem;
  margin-bottom: 1rem;
`;

const BackButton = styled.button`
  background: #000;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: #333;
    transform: translateY(-1px);
  }
`;

const RegistrationWizard: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentStep, loadRegistration, registrationData, isLoading, error } = useRegistration();

  useEffect(() => {
    if (id) {
      const registrationId = parseInt(id);
      loadRegistration(registrationId);
    }
  }, [id]); // Removed loadRegistration from dependencies

  const handleBackToRequests = () => {
    navigate('/my-requests');
  };

  if (isLoading) {
    return (
      <WizardContainer>
        <WizardContent>
          <LoadingContainer>
            Loading registration data...
          </LoadingContainer>
        </WizardContent>
      </WizardContainer>
    );
  }

  if (error) {
    return (
      <WizardContainer>
        <WizardContent>
          <ErrorContainer>
            <ErrorMessage>{error}</ErrorMessage>
            <BackButton onClick={handleBackToRequests}>
              ← Back to My Requests
            </BackButton>
          </ErrorContainer>
        </WizardContent>
      </WizardContainer>
    );
  }

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <BusinessActivity />;
      case 2:
        return <CompanyName />;
      case 3:
        return <OwnersInfo />;
      case 4:
        return <Charter />;
      case 5:
        return <AdditionalServices />;
      case 6:
        return <Summary />;
      default:
        return <BusinessActivity />;
    }
  };

  return (
    <WizardContainer>
      <StepIndicator />
      <WizardContent>
        {renderStep()}
      </WizardContent>
    </WizardContainer>
  );
};

export default RegistrationWizard;